// src/pages/profile-settings/patient/EditPhone.jsx
import React from "react";
import { useNavigate } from "react-router-dom";

import TopBar from "@/sot/primitives/TopBar.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import InputPhoneSADC from "@/components/auth/InputPhoneSADC.jsx";
import InputOtp from "@/components/ui/InputOtp.jsx";
import { splitE164ToCcLocal, toE164FromCcLocal } from "@/utils/phone.js";
import supabase from "@/services/supabaseClient";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { PATIENT_SETTINGS_PROFILE_ROUTE, DOCTOR_SETTINGS_PROFILE_ROUTE } from "@/pages/profile-settings/settingsNavigation.js";
import { verificationRequest, resolvePhoneVerificationState, phoneSavePatch } from '@/features/phone-verification/phoneVerificationService.js';
import usePhoneVerificationCapabilities from '@/features/phone-verification/usePhoneVerificationCapabilities.js';

import {
  loadPatientIntroPreview,
  savePatientIntroPreview,
} from "@/lib/patientIntroPreview";

// Dev-only bypass (kept behind env flag)
const BYPASS =
  import.meta.env.DEV && import.meta.env.VITE_BYPASS_VERIFICATION === "1";
const PENDING_PHONE_OTP_KEY = "afrimed:phone_otp_pending";

function isFrenchLanguageCode(value = "") {
  return String(value || "").toLowerCase().startsWith("fr");
}

function isStagingEvaluationPhoneVerification() {
  try {
    const host = String(window.location.hostname || "").toLowerCase();
    return (
      import.meta.env.DEV ||
      import.meta.env.MODE === "staging" ||
      String(import.meta.env.VITE_APP_ENV || "").trim().toLowerCase() === "staging" ||
      host === "staging.afrimed.tech" ||
      host.endsWith("--afrimed-staging.netlify.app")
    );
  } catch {
    return false;
  }
}

// tiny helper: best-effort split e164 into {cc, local}
function splitE164(e164) {
  return splitE164ToCcLocal(e164);
}

function rememberPendingOtp(phone) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      PENDING_PHONE_OTP_KEY,
      JSON.stringify({
        phone,
        expiresAt: Date.now() + 5 * 60 * 1000,
      })
    );
  } catch {
    // non-critical UI continuity only
  }
}

function readPendingOtp(phone) {
  if (typeof window === "undefined" || !phone) return false;
  try {
    const raw = window.sessionStorage.getItem(PENDING_PHONE_OTP_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (String(parsed?.phone || "") !== String(phone)) return false;
    if (Number(parsed?.expiresAt || 0) <= Date.now()) {
      window.sessionStorage.removeItem(PENDING_PHONE_OTP_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function clearPendingOtp() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(PENDING_PHONE_OTP_KEY);
  } catch {
    // non-critical UI continuity only
  }
}

export default function EditPhone({ role = "patient" }) {
  const profileTable = role === "doctor" ? "doctor_profiles" : "patient_profiles";
  const profileRoute = role === "doctor" ? DOCTOR_SETTINGS_PROFILE_ROUTE : PATIENT_SETTINGS_PROFILE_ROUTE;
  const nav = useNavigate();
  const { languageCode, t } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);

  const [cc, setCc] = React.useState("+27");
  const [localNumber, setLocalNumber] = React.useState(""); // digits only
  const [agreed, setAgreed] = React.useState(false);

  const [err, setErr] = React.useState("");
  const [toast, setToast] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [otpCode, setOtpCode] = React.useState("");
  const [verificationPhone, setVerificationPhone] = React.useState("");
  const [verificationSent, setVerificationSent] = React.useState(false);
  const [verifiedAt, setVerifiedAt] = React.useState("");
  const [sendingCode, setSendingCode] = React.useState(false);
  const [deliveryChannel, setDeliveryChannel] = React.useState("whatsapp");
  const [verifyingCode, setVerifyingCode] = React.useState(false);
  const [reverificationTesting, setReverificationTesting] = React.useState(false);

  // Server truth (Supabase Auth)
  const [authUser, setAuthUser] = React.useState(null);
  const authPhone = authUser?.phone || "";
  const authPhoneConfirmedAt =
    authUser?.phone_confirmed_at || authUser?.phoneConfirmedAt || null;

  // Derived inputs
  const len = (localNumber || "").length;

  const e164Current = toE164FromCcLocal(cc, localNumber) || "";
  const numberLooksOk = !!e164Current;

  const capabilities = usePhoneVerificationCapabilities(e164Current);
  const verificationState = resolvePhoneVerificationState({ phone: e164Current,
    savedPhone: verificationPhone, verifiedAt, authPhone, authConfirmedAt: authPhoneConfirmedAt,
    capabilities, testing: reverificationTesting, pending: verificationSent });
  const alreadyVerifiedThisNumber = verificationState.verified;
  const savedPhoneExists = !!verificationPhone || !!authPhone;
  const phoneVerificationAvailable = verificationState.available;
  const showAlreadyVerifiedThisNumber = verificationState.state === 'verified';
  const showVerificationActions = verificationState.canSend && !verificationSent;
  const canTestVerification = verificationState.canTest && isStagingEvaluationPhoneVerification();

  const canSave =
    (agreed || BYPASS) && (numberLooksOk || BYPASS) && !!e164Current && !saving;

  function handleLocalChange(digitsOnly) {
    setLocalNumber(String(digitsOnly || "").replace(/\D/g, ""));
    setErr("");
    setVerificationSent(false);
    setOtpCode("");
    clearPendingOtp();
  }

  function handleCcChange(nextCc) {
    setCc(nextCc);
    setErr("");
    setVerificationSent(false);
    setOtpCode("");
    clearPendingOtp();
  }

  function showToast(msg, opts = {}) {
    setToast(msg);
    window.clearTimeout(showToast._t || 0);
    showToast._t = window.setTimeout(() => {
      setToast("");
      if (opts.after) opts.after();
    }, opts.duration ?? 3000);
  }

  // Only used as a UI convenience in your app; NOT the source of truth.
  function persistPhoneCaptured(e164) {
    if (role !== "patient") return;
    const current = loadPatientIntroPreview() || {};
    savePatientIntroPreview({
      ...current,
      phone: e164,
      phoneVerified: false, // explicitly NOT verified in this phase
    });
  }

  async function refreshAuthUser() {
    const { data, error } = await supabase.auth.getUser();
    if (!error) setAuthUser(data?.user || null);
  }

  // Load server truth once; also use it to prefill the UI
  React.useEffect(() => {
    let mounted = true;

    (async () => {
      const { data, error } = await supabase.auth.getUser();
      if (!mounted) return;
      if (!error) {
        const u = data?.user || null;
        setAuthUser(u);

        // Prefill from Auth phone first; fallback to patient profile phone.
        const authParts = splitE164(u?.phone);
        if (authParts?.cc && authParts?.local) {
          setCc(authParts.cc);
          setLocalNumber(authParts.local);
        }

        if (!u?.id) return;

        const { data: row, error: profileErr } = await supabase
          .from(profileTable)
          .select(role === "doctor" ? "phone,phone_verified_at" : "phone,phone_number,phone_verified_at")
          .eq("user_id", u.id)
          .maybeSingle();

        if (profileErr || !row) return;

        const profileParts = splitE164(row?.phone || row?.phone_number || "");
        if (profileParts?.cc && profileParts?.local) {
          setCc(profileParts.cc);
          setLocalNumber(profileParts.local);
          setVerificationPhone(row?.phone || row?.phone_number || "");
          setVerifiedAt(row?.phone_verified_at || "");
          if (
            phoneVerificationAvailable &&
            !row?.phone_verified_at &&
            readPendingOtp(row?.phone || row?.phone_number || "")
          ) {
            setVerificationSent(true);
          }
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [role, profileTable]);

  // ────────────────────────────────────────────────────────────────
  async function sendVerificationCode(e164Final, channel = deliveryChannel) {
    setSendingCode(true);
    try {
      const result = await verificationRequest('send', { phone: e164Final, channel, role, languageCode });
      setDeliveryChannel(channel);
      return result;
    } finally { setSendingCode(false); }
  }

  async function handleVerifyCode(nextCode = otpCode) {
    const clean = String(nextCode || "").replace(/\D/g, "").slice(0, 6);
    setOtpCode(clean);
    setErr("");
    if (clean.length !== 6 || verifyingCode || !verificationPhone) return;

    setVerifyingCode(true);
    try {
      await verificationRequest('verify', { phone: verificationPhone, code: clean, role });
      const { data: row, error } = await supabase.from(profileTable).select('phone,phone_verified_at').eq('user_id', authUser.id).single();
      if (error || !row?.phone_verified_at || row.phone !== verificationPhone) throw new Error('verification_not_persisted');
      setVerifiedAt(row.phone_verified_at);
      setVerificationSent(false);
      setReverificationTesting(false);
      clearPendingOtp();
      showToast(t("settings.edit.phone.verifiedToast", isFrench ? "Numéro de téléphone vérifié" : "Phone number verified"), {
        duration: 2200,
      });
    } catch {
      setErr(
        t(
          "settings.edit.phone.invalidCode",
          "That code didn't verify. Please check it and try again."
        )
      );
    } finally {
      setVerifyingCode(false);
    }
  }

  // Save phone, then verify through the existing OTP backend.
  // ────────────────────────────────────────────────────────────────
  async function handleSavePhone() {
    if (saving) return;
    setErr("");

    if (!authUser?.id) {
      setErr(
        t(
          "settings.edit.common.sessionNotReady",
          "Your session isn't ready yet. Please try again."
        )
      );
      return;
    }

    const e164Final = toE164FromCcLocal(cc, localNumber);
    if (!e164Final) {
      setErr(
        t(
          "settings.edit.phone.invalidPhone",
          isFrench ? "Veuillez d’abord saisir un numéro de téléphone valide." : "Please enter a valid phone number first."
        )
      );
      return;
    }

    if (!BYPASS) {
      if (!agreed) {
        setErr(
          t(
            "settings.edit.phone.mustAccept",
            isFrench ? "Veuillez d’abord accepter les conditions." : "Please accept the terms first."
          )
        );
        return;
      }
      if (!numberLooksOk) {
        setErr(
          t(
            "settings.edit.phone.invalidPhoneLength",
            isFrench ? "Veuillez saisir un numéro de téléphone valide." : "Please enter a valid phone number."
          )
        );
        return;
      }
    }

    setSaving(true);

    const watchdog = window.setTimeout(() => {
      setSaving(false);
      setErr(
        t(
          "settings.edit.phone.timeout",
          isFrench ? "L’enregistrement prend trop de temps. Veuillez appuyer à nouveau sur Enregistrer." : "Saving is taking too long. Please tap Save again."
        )
      );
    }, 15000);

    try {
      // Upsert is more reliable than update (row may not exist yet).
      // Requires patient_profiles has a unique constraint on user_id (common in your schema pattern).
      const { data: row, error: upErr } = await supabase
        .from(profileTable)
        .upsert(
          {
            user_id: authUser.id,
            ...phoneSavePatch({ phone: e164Final, savedPhone: verificationPhone, verifiedAt }),
          },
          { onConflict: "user_id" }
        )
        .select("id")
        .maybeSingle();

      if (upErr) throw upErr;
      if (!row?.id) {
        throw new Error(
          t(
            "settings.edit.phone.saveFailed",
            isFrench ? "Impossible d’enregistrer le numéro de téléphone. Veuillez réessayer." : "We couldn't save the phone number. Please try again."
          )
        );
      }

      persistPhoneCaptured(e164Final);

      setVerificationPhone(e164Final);
      if (e164Final !== verificationPhone) setVerifiedAt("");
      setOtpCode("");
      clearPendingOtp();

      showToast(t("settings.edit.phone.updated", isFrench ? "Numéro de téléphone enregistré" : "Phone number saved"), {
        duration: 1800,
      });
      await refreshAuthUser();
    } catch (e) {
      setErr(
        e?.code === "rate_limited"
          ? t(
              "settings.edit.phone.rateLimited",
              isFrench ? "Veuillez patienter avant de demander un autre code." : "Please wait before requesting another code."
            )
          : e?.message ||
              t(
                "settings.edit.phone.saveFailed",
                isFrench ? "Impossible d’enregistrer le numéro de téléphone. Veuillez réessayer." : "We couldn't save the phone number. Please try again."
              )
      );
    } finally {
      window.clearTimeout(watchdog);
      setSaving(false);
    }
  }

  return (
    <SettingsScreenShell>
      <TopBar
        title={t("settings.edit.phone.title", isFrench ? "Modifier le téléphone" : "Edit your phone number")}
        subtitle={t(
          "settings.edit.phone.subtitle",
          isFrench ? "Ajoutez votre numéro de téléphone afin que nous puissions vous contacter si nécessaire." : "Add your phone number so we can contact you when needed"
        )}
        onBack={() => nav(profileRoute)}
      />

      <div className="mt-5 md:mt-6 space-y-4">
        <InputPhoneSADC
          value={localNumber}
          onChange={handleLocalChange}
          cc={cc}
          onChangeCc={handleCcChange}
          tone="surface"
        />

        {showAlreadyVerifiedThisNumber ? (
          <div className="space-y-3">
            <div className="text-body-medium text-on-surface-var">
              {t(
                "settings.edit.phone.verified",
                isFrench ? "Ce numéro de téléphone est déjà vérifié." : "This phone number is already verified."
              )}
            </div>
            {canTestVerification && (
              <button
                type="button"
                onClick={() => {
                  setErr("");
                  setOtpCode("");
                  setVerificationSent(false);
                  setReverificationTesting(true);
                }}
                className="h-9 px-3 rounded-lg text-body-medium"
                style={{
                  background: "rgba(77,163,255,0.12)",
                  color: "rgb(var(--afrimed-primary))",
                  boxShadow: "0 0 0 1px rgba(29,79,215,0.18) inset",
                }}
              >
                {isFrench ? "Tester la vérification" : "Test verification"}
              </button>
            )}
          </div>
        ) : (showVerificationActions || verificationSent) ? null : (
          <>
            <div className="text-body-medium text-on-surface-var">
              {!phoneVerificationAvailable && savedPhoneExists
                ? t(
                    "settings.edit.phone.deliveryUnavailableSaved",
                    isFrench ? "Votre numéro de téléphone est enregistré, mais la vérification est temporairement indisponible. " : "Your phone number is saved. Verification delivery is temporarily unavailable."
                  )
                : !phoneVerificationAvailable
                  ? t(
                    "settings.edit.phone.deliveryUnavailableNoPhone",
                    isFrench ? "Ajoutez votre numéro de téléphone afin qu’AfriMed™ puisse vous contacter si nécessaire. La vérification par téléphone est temporairement indisponible ." : "Add your phone number so AfriMed™ can contact you when needed. Verification delivery is temporarily unavailable."
                    )
                  : t(
                      "settings.edit.phone.verificationAvailable",
                      isFrench ? "Enregistrez ce numéro, puis choisissez WhatsApp ou SMS pour le vérifier." : "Save this phone number, then choose an available delivery channel to verify it."
                    )}
            </div>
            <label className="mt-2 flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={!!agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <span className="text-body-medium text-on-surface-var">
                {t(
                  "settings.edit.phone.termsLead",
                  isFrench ? "En cochant cette case, je confirme avoir lu et accepté les" : "By checking this box, I confirm that I have read and agree to the"
                )}{" "}
                <a
                  className="text-primary no-underline"
                  href="/legal/terms"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("settings.edit.phone.termsLink", isFrench ? "Conditions d’utilisation" : "Terms of Service")}
                </a>{" "}
                {t("settings.edit.phone.termsJoiner", isFrench ? "et la" : "and")}{" "}
                <a
                  className="text-primary no-underline"
                  href="/legal/privacy"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("settings.edit.phone.privacyLink", isFrench ? "Politique de confidentialité" : "Privacy Policy")}
                </a>
                {t("settings.edit.phone.termsEnd", ".")}
              </span>
            </label>

            <div className="relative z-[60] mt-3 flex items-center justify-start pointer-events-auto">
              <button
                type="button"
                disabled={!canSave}
                onClick={handleSavePhone}
                className="h-9 min-w-[104px] shrink-0 whitespace-nowrap rounded-lg px-4 text-body-medium disabled:opacity-60"
                style={{
                  background: "rgba(77,163,255,0.12)",
                  color: "rgb(var(--afrimed-primary))",
                  boxShadow: "0 0 0 1px rgba(29,79,215,0.18) inset",
                }}
              >
                {saving
                  ? t("common.saving", isFrench ? "Enregistrement..." : "Saving...")
                  : t("settings.edit.phone.savePhone", isFrench ? "Enregistrer le téléphone" : "Save phone")}
              </button>
            </div>
          </>
        )}

        {!!err && (
          <p className="mt-2 text-sm text-[rgb(var(--afrimed-danger))]">{err}</p>
        )}

        {showVerificationActions && (
          <div className="pt-1">
            <p className="text-body-medium text-on-surface">
              {reverificationTesting
                ? (isFrench ? "Test de vérification" : "Verification test")
                : t("settings.edit.phone.savedReady", isFrench ? "Numéro de téléphone enregistré" : "Phone number saved")}
            </p>
            <p className="mt-1 text-body-small text-on-surface-var">
              {reverificationTesting
                ? (isFrench
                    ? "Votre numéro reste vérifié pendant ce test de livraison."
                    : "Your phone remains verified while this delivery test is in progress.")
                : t(
                    "settings.edit.phone.savedReadyCopy",
                    isFrench ? "Envoyez un code de vérification pour continuer la vérification de {phone}." : "Send a verification code to continue verifying {phone}.",
                    { phone: verificationPhone }
                  )}
            </p>
            {capabilities?.whatsapp && <button
              type="button"
              disabled={sendingCode}
              onClick={async () => {
                try {
                  setErr("");
                  await sendVerificationCode(verificationPhone, "whatsapp");
                  setVerificationSent(true);
                  rememberPendingOtp(verificationPhone);
                  setOtpCode("");
                  showToast(t("settings.edit.phone.codeSent", isFrench ? "Code de vérification envoyé" : "Verification code sent"));
                } catch (e) {
                  setErr(
                    e?.code === "rate_limited"
                      ? t(
                          "settings.edit.phone.rateLimited",
                          isFrench ? "Veuillez patienter avant de demander un autre code." : "Please wait before requesting another code."
                        )
                      : t(
                          "settings.edit.phone.sendFailed",
                          isFrench ? "Impossible d’envoyer un code de vérification. Veuillez réessayer." : "We couldn't send a verification code. Please try again."
                        )
                  );
                }
              }}
              className="mt-4 h-9 px-3 rounded-lg text-body-medium disabled:opacity-60"
              style={{
                background: "rgba(77,163,255,0.12)",
                color: "rgb(var(--afrimed-primary))",
                boxShadow: "0 0 0 1px rgba(29,79,215,0.18) inset",
              }}
            >
              {sendingCode
                ? t("settings.edit.phone.sending", isFrench ? "Envoi..." : "Sending...")
                : (isFrench ? "Envoyer le code par WhatsApp" : "Send code with WhatsApp")}
            </button>}
            {capabilities?.sms && <button
              type="button"
              disabled={sendingCode}
              onClick={async () => {
                try {
                  setErr("");
                  await sendVerificationCode(verificationPhone, "sms");
                  setVerificationSent(true);
                  rememberPendingOtp(verificationPhone);
                  setOtpCode("");
                  showToast(isFrench ? "Code envoyé par SMS" : "Verification code sent by SMS");
                } catch {
                  setErr(isFrench ? "Impossible d’envoyer un code de vérification. Veuillez réessayer." : "We couldn't send a verification code. Please try again.");
                }
              }}
              className="ml-3 mt-4 h-9 px-3 rounded-lg text-body-medium disabled:opacity-60"
              style={{ background: "rgba(77,163,255,0.12)", color: "rgb(var(--afrimed-primary))", boxShadow: "0 0 0 1px rgba(29,79,215,0.18) inset" }}
            >
              {isFrench ? "Envoyer par SMS" : "Send code by SMS"}
            </button>}
          </div>
        )}

        {reverificationTesting && verificationSent && <p className="text-body-small text-on-surface-var">{isFrench ? "Votre téléphone reste vérifié pendant ce test de livraison." : "Your phone remains verified while this delivery test is in progress."}</p>}
        {phoneVerificationAvailable && verificationSent && (!alreadyVerifiedThisNumber || reverificationTesting) && (
          <div className="pt-1">
            <p className="text-body-medium text-on-surface">
              {t("settings.edit.phone.enterCode", isFrench ? "Saisir le code de vérification" : "Enter verification code")}
            </p>
            <p className="mt-1 text-body-small text-on-surface-var">
              {t(
                "settings.edit.phone.codeSentTo",
                isFrench ? "Nous avons envoyé un code à 6 chiffres à {phone}. Il expire dans 5 minutes." : "We sent a 6-digit code to {phone}. It expires in 5 minutes.",
                { phone: verificationPhone }
              )}
            </p>
            <div className="mt-4">
              <InputOtp
                value={otpCode}
                onChange={(next) => {
                  const clean = String(next || "").replace(/\D/g, "").slice(0, 6);
                  setOtpCode(clean);
                  setErr("");
                }}
                onComplete={handleVerifyCode}
                tone="surface"
                align="start"
                autoFocus
              />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                disabled={otpCode.length !== 6 || verifyingCode}
                onClick={() => handleVerifyCode()}
                className="h-9 px-3 rounded-lg text-body-medium disabled:opacity-60"
                style={{
                  background: "rgba(77,163,255,0.12)",
                  color: "rgb(var(--afrimed-primary))",
                  boxShadow: "0 0 0 1px rgba(29,79,215,0.18) inset",
                }}
              >
                {verifyingCode
                  ? t("settings.edit.phone.verifying", isFrench ? "Vérification..." : "Verifying...")
                  : t("settings.edit.phone.verify", isFrench ? "Vérifier" : "Verify")}
              </button>
              <button
                type="button"
                disabled={sendingCode || saving}
                onClick={async () => {
                  try {
                    setErr("");
                    await sendVerificationCode(verificationPhone);
                    rememberPendingOtp(verificationPhone);
                    setOtpCode("");
                    showToast(t("settings.edit.phone.codeSent", isFrench ? "Code de vérification envoyé" : "Verification code sent"));
                  } catch (e) {
                    setErr(
                      e?.code === "rate_limited"
                        ? t(
                            "settings.edit.phone.rateLimited",
                            isFrench ? "Veuillez patienter avant de demander un autre code." : "Please wait before requesting another code."
                          )
                        : t(
                            "settings.edit.phone.sendFailed",
                            isFrench ? "Impossible d’envoyer un code de vérification. Veuillez réessayer." : "We couldn't send a verification code. Please try again."
                          )
                    );
                  }
                }}
                className="text-label-large text-primary disabled:opacity-50"
              >
                {sendingCode
                  ? t("settings.edit.phone.sending", isFrench ? "Envoi..." : "Sending...")
                  : t("settings.edit.phone.resend", isFrench ? "Renvoyer" : "Resend")}
              </button>
              {capabilities?.fallbackPermitted && deliveryChannel !== "sms" && <button
                type="button" disabled={sendingCode || verifyingCode}
                className="text-label-large text-primary disabled:opacity-50"
                onClick={async () => {
                  try {
                    setErr("");
                    await sendVerificationCode(verificationPhone, "sms");
                    rememberPendingOtp(verificationPhone); setOtpCode("");
                  } catch { setErr(isFrench ? "Impossible de demander le code par SMS. Réessayez." : "We could not request an SMS code. Please try again."); }
                }}>
                {isFrench ? "Envoyer par SMS" : "Send code by SMS"}
              </button>}
            </div>
          </div>
        )}

        {!numberLooksOk &&
          len > 0 &&
          !err &&
          !BYPASS &&
          !showAlreadyVerifiedThisNumber && (
            <p className="mt-2 text-sm text-[rgb(var(--afrimed-danger))]">
            {t(
              "settings.edit.phone.invalidPhoneLength",
              isFrench ? "Veuillez saisir un numéro de téléphone valide." : "Please enter a valid phone number."
            )}
            </p>
          )}
      </div>

      <Snackbar open={!!toast} onClose={() => setToast("")}>
        {toast}
      </Snackbar>
    </SettingsScreenShell>
  );
}
